import styled, { css } from 'styled-components';

// The admin program screen's own visual vocabulary, built on the shared primitives in ./ui (same
// colours, same square corners, same Roboto): this file only
// adds what that screen does not have - the dense timeline, the spot rows, the side panel and the
// sticky selection bar.
//
// Contrast (WCAG AA): body text #334155 / #475569 on white or #f8fafc is >= 6:1; muted #64748b on
// white is 4.8:1; the state tags are >= 6:1 (see STATUS_META in programAdminModel); #47ABDB is used
// for borders and lines only, never as text on white.
export const NAVY = '#111D4E';
export const ACCENT = '#47ABDB';
export const ACCENT_DARK = '#0056b3';
const LINE = '#e2e8f0';
const LINE_SOFT = '#eef2f7';
const MUTED = '#64748b';
const BODY = '#334155';
const FLAT = '#f8fafc';

// The area below the event dropdown: the timeline and, when something is selected, the side panel.
export const Workspace = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$panel ? `minmax(0, 1fr) ${p.$wide ? '34rem' : '26rem'}` : 'minmax(0, 1fr)')};
  gap: 1.25rem;
  align-items: start;
`;

export const Column = styled.div`min-width: 0;`;

// ------------------------------------------------------------------ toolbar (filters)

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.85rem;
  margin-bottom: 1rem;
  background: ${FLAT};
  border: 1px solid ${LINE};
`;

export const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${BODY};
  cursor: pointer;
  padding: 0.3rem 0.6rem;
  border: 1px solid ${(p) => (p.$active ? ACCENT_DARK : '#cbd5e1')};
  background: ${(p) => (p.$active ? '#eff9fe' : '#fff')};

  input { cursor: pointer; margin: 0; accent-color: ${ACCENT_DARK}; }
`;

export const Count = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  color: ${(p) => (p.$tone === 'attention' ? '#92400e' : BODY)};
  background: ${(p) => (p.$tone === 'attention' ? '#fef3c7' : '#eef2f7')};
  padding: 0 0.35rem;
`;

export const ToolbarNote = styled.span`font-size: 0.78rem; color: ${MUTED}; margin-left: auto;`;

// ------------------------------------------------------------------ days and slots

export const DayTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  border-bottom: 2px solid ${LINE};
  margin-bottom: 1.1rem;
`;

export const DayTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 0;
  background: transparent;
  font: inherit;
  font-weight: 700;
  color: ${(p) => (p.$active ? NAVY : MUTED)};
  padding: 0.55rem 0.9rem;
  margin-bottom: -2px;
  border-bottom: 2px solid ${(p) => (p.$active ? ACCENT : 'transparent')};
  cursor: pointer;

  &:hover { color: ${NAVY}; }
`;

export const SlotRow = styled.div`
  display: grid;
  grid-template-columns: 4.5rem minmax(0, 1fr);
  gap: 0 0.9rem;
  margin-bottom: 0.85rem;
`;

export const SlotTime = styled.div`
  font-weight: 800;
  font-size: 0.95rem;
  color: ${NAVY};
  font-variant-numeric: tabular-nums;
  padding-top: 0.55rem;
`;

export const BlockStack = styled.div`display: flex; flex-direction: column; gap: 0.6rem;`;

// A top-level block. Breaks, arrival and social items are quieter, exactly like the public card.
export const BlockCard = styled.article`
  background: ${(p) => (p.$quiet ? FLAT : '#fff')};
  border: 1px solid ${(p) => (p.$selected ? ACCENT : LINE)};
  border-left: 4px solid ${(p) => (p.$quiet ? '#94a3b8' : ACCENT)};
  box-shadow: ${(p) => (p.$selected ? '0 0 0 2px #cbe8f7' : '0 1px 3px rgba(15,23,42,.05)')};
  padding: 0.55rem 0.75rem 0.65rem;
`;

export const BlockHead = styled.button`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.4rem 0.6rem;
  width: 100%;
  border: 0;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
  padding: 0.15rem 0;
  color: ${NAVY};

  &:hover { background: #f6fafd; }
`;

export const BlockKind = styled.span`
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${BODY};
  background: #eef2f7;
  padding: 0.1rem 0.4rem;
`;

export const BlockTitle = styled.span`
  font-size: ${(p) => (p.$depth === 0 ? '1.02rem' : p.$depth === 1 ? '0.95rem' : '0.9rem')};
  font-weight: ${(p) => (p.$depth === 2 ? 600 : 700)};
  line-height: 1.35;
`;

// The entry's subtitle under its title: muted, smaller, and always wrapped in full (never an
// ellipsis - it is content, not a name in a row).
export const BlockSubtitle = styled.span`
  flex-basis: 100%;
  font-size: 0.8rem;
  line-height: 1.4;
  color: ${MUTED};
  white-space: normal;
  overflow-wrap: anywhere;
`;

export const BlockMeta = styled.span`
  font-size: 0.76rem;
  color: ${MUTED};
  font-variant-numeric: tabular-nums;
`;

// The admin-side eyebrow of an entry: "TALK 1", "INNOVATION" (design §4.4, the label).
export const EntryLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${MUTED};
`;

export const RoomTag = styled.span`
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #075985;
  background: #e0f2fe;
  padding: 0.1rem 0.45rem;
`;

export const InternalTag = styled.span`
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #92400e;
  background: #fef3c7;
  padding: 0.1rem 0.45rem;
`;

export const RemovedTag = styled.span`
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;
  background: #e2e8f0;
  padding: 0.1rem 0.45rem;
`;

// Children (depth 1) and grandchildren (depth 2) of a block, indented under it.
export const ChildStack = styled.div`
  margin: 0.5rem 0 0 ${(p) => (p.$depth > 1 ? '1.1rem' : '0')};
  padding-left: 0.7rem;
  border-left: 2px solid ${LINE};
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

export const ChildRow = styled.div`
  background: ${(p) => (p.$selected ? '#eff9fe' : FLAT)};
  border: 1px solid ${(p) => (p.$selected ? ACCENT : LINE_SOFT)};
  padding: 0.4rem 0.6rem 0.5rem;
`;

export const RemovedCard = styled(BlockCard)`
  opacity: 0.6;
  filter: grayscale(1);
  border-left-color: #94a3b8;
`;

export const EmptyDay = styled.p`color: ${MUTED}; padding: 1.5rem 0; text-align: center;`;

// ------------------------------------------------------------------ display elements

const borderImage = 'border-image: linear-gradient(to bottom, #4e4094, #14cbde) 1;';

export const ElementText = styled.div`
  font-size: 0.85rem;
  color: ${BODY};
  line-height: 1.5;
  white-space: pre-line;
  padding: 0.3rem 0;
  ${(p) => p.$box && css`background: #eef7fb; border: 1px solid #90C4D9; padding: 0.55rem 0.7rem;`}
`;

export const ElementQuote = styled.blockquote`
  margin: 0;
  padding: 0.25rem 0 0.25rem 0.7rem;
  border-left: 3px solid;
  ${borderImage}
  font-size: 0.9rem;
  font-style: italic;
  color: ${BODY};
  line-height: 1.5;
  ${(p) => p.$box && css`background: #eef7fb; padding: 0.55rem 0.7rem;`}
`;

export const ElementHeading = styled.div`
  font-size: 0.86rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${NAVY};
  ${(p) => p.$box && css`background: #eef7fb; border: 1px solid #90C4D9; padding: 0.4rem 0.7rem;`}
`;

// ------------------------------------------------------------------ one spot

export const SpotRow = styled.div`
  display: grid;
  grid-template-columns: auto 6.5rem minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.35rem 0.6rem;
  padding: 0.3rem 0.35rem;
  border: 1px dashed transparent;
  border-left: 3px solid ${(p) => p.$border || 'transparent'};
  background: ${(p) => (p.$selected ? '#eff9fe' : 'transparent')};

  ${(p) => p.$open && css`
    border: 1px dashed #d97706;
    border-left: 3px solid #d97706;
    background: #fffdf5;
  `}

  ${(p) => p.$removed && css`
    opacity: 0.6;
    filter: grayscale(1);
    background: ${FLAT};
    border: 1px solid ${LINE};
  `}
`;

export const SpotCheck = styled.input`
  margin: 0;
  accent-color: ${ACCENT_DARK};
  cursor: pointer;
`;

export const RoleCell = styled.span`
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${BODY};
`;

export const NameCell = styled.div`min-width: 0;`;

export const SpotName = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 700;
  color: ${NAVY};
  text-align: left;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: rgba(17, 29, 78, 0.3);
  text-underline-offset: 2px;

  &:hover { text-decoration-color: ${ACCENT_DARK}; }
`;

// A spot without a person: the label is what the program prints meanwhile.
export const OpenLabel = styled.span`
  font-size: 0.88rem;
  font-weight: 700;
  font-style: italic;
  color: #92400e;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dashed;
  text-underline-offset: 2px;
`;

export const SpotNote = styled.span`font-size: 0.76rem; color: ${MUTED}; margin-left: 0.4rem;`;

export const SpotGroup = styled.span`
  display: block;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${MUTED};
`;

export const StateTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.15rem 0.45rem;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  border-left: 3px solid ${(p) => p.$border};
  white-space: nowrap;
`;

export const OpenTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.15rem 0.45rem;
  background: #fef3c7;
  color: #92400e;
  border-left: 3px solid #d97706;
  white-space: nowrap;
`;

// A declined spot with a suggested replacement: teal, distinct from the state colours (violet, green,
// red, orange, grey) and from the amber "needs filling"; #115e59 on #ccfbf1 is about 6.9:1.
export const SuggestionTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  padding: 0.15rem 0.45rem;
  background: #ccfbf1;
  color: #115e59;
  border-left: 3px solid #0f766e;
  max-width: 100%;
`;

export const SpotActions = styled.span`display: inline-flex; gap: 0.3rem; align-items: center;`;

// The state tags of one spot: the state word, plus "needs filling" / "removed" where they apply.
export const SpotTags = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: center;
  justify-content: flex-end;
`;

// The dense row buttons; the dialog keeps the big Speaker Management Button.
export const MiniButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid ${(p) => (p.$danger ? '#fecaca' : '#cbd5e1')};
  background: #fff;
  color: ${(p) => (p.$danger ? '#991b1b' : BODY)};
  font: inherit;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.22rem 0.45rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { border-color: ${(p) => (p.$danger ? '#b91c1c' : ACCENT_DARK)}; color: ${NAVY}; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

export const MiniButtonPrimary = styled(MiniButton)`
  background: ${ACCENT_DARK};
  border-color: ${ACCENT_DARK};
  color: #fff;

  &:hover:not(:disabled) { background: #004494; color: #fff; }
`;

// ------------------------------------------------------------------ side panel

export const Panel = styled.aside`
  position: sticky;
  top: 1rem;
  background: #fff;
  border: 1px solid ${LINE};
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.75rem 0.9rem 0.6rem;
  border-bottom: 1px solid ${LINE_SOFT};
  background: ${FLAT};
`;

export const PanelEyebrow = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${MUTED};
`;

export const PanelTitle = styled.h2`font-size: 1rem; margin: 0.1rem 0 0; color: ${NAVY};`;

export const PanelClose = styled.button`
  border: 0;
  background: none;
  color: ${MUTED};
  cursor: pointer;
  padding: 0.15rem;
  line-height: 0;

  &:hover { color: ${NAVY}; }
`;

export const PanelSection = styled.div`padding: 0.75rem 0.9rem; border-bottom: 1px solid ${LINE_SOFT};`;

export const PanelSectionTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${MUTED};
  margin-bottom: 0.4rem;
`;

export const PanelRow = styled.div`display: flex; gap: 0.5rem; font-size: 0.82rem; color: ${BODY}; line-height: 1.55;`;

export const PanelLabel = styled.span`flex: 0 0 7.5rem; color: ${MUTED};`;

export const PanelActions = styled.div`display: flex; flex-wrap: wrap; gap: 0.4rem;`;

export const PanelHint = styled.p`font-size: 0.76rem; color: ${MUTED}; margin: 0.5rem 0 0; line-height: 1.5;`;

// The invitation's email dates in the spot panel: one framed block, so "when did they last hear from
// us" is found at a glance.
export const MailBox = styled.div`
  margin: 0.5rem 0 0.2rem;
  padding: 0.45rem 0.6rem;
  background: #eff6ff;
  border-left: 3px solid #3b82f6;
`;

export const SuggestionBox = styled.div`
  border: 1px solid #99f6e4;
  border-left: 4px solid #0f766e;
  background: #f0fdfa;
  padding: 0.6rem 0.7rem;
`;

// The declined spot's suggestion box: who was suggested, with the one prominent action for it.
export const SuggestionIntro = styled.div`margin: 0 0 0.5rem; font-size: 0.8rem; color: #134e4a; line-height: 1.5;`;

export const HistoryToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${BODY};
  cursor: pointer;

  &:hover { color: ${NAVY}; }
`;

export const HistoryList = styled.ol`
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const HistoryRow = styled.li`
  font-size: 0.78rem;
  color: ${BODY};
  line-height: 1.5;
  padding-left: 0.6rem;
  border-left: 2px solid ${LINE};
`;

export const HistoryWhen = styled.span`color: ${MUTED}; font-variant-numeric: tabular-nums;`;

// ------------------------------------------------------------------ sticky selection bar

export const ActionBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.6rem 0.9rem;
  background: ${NAVY};
  color: #fff;
  box-shadow: 0 -6px 18px rgba(15, 23, 42, 0.18);
`;

export const ActionBarCount = styled.span`font-weight: 800; font-size: 0.88rem;`;

export const ActionBarButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 0;
  background: ${ACCENT_DARK};
  color: #fff;
  font: inherit;
  font-weight: 700;
  font-size: 0.85rem;
  padding: 0.45rem 0.8rem;
  cursor: pointer;

  &:hover { background: #004494; }
`;

export const ActionBarClear = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: transparent;
  color: #fff;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.35rem 0.6rem;
  cursor: pointer;
  margin-left: auto;

  &:hover { background: rgba(255, 255, 255, 0.12); }
`;

// ------------------------------------------------------------------ People area table

export const TableScroll = styled.div`overflow-x: auto;`;

export const Table = styled.table`width: 100%; border-collapse: collapse;`;

export const Th = styled.th`
  text-align: left;
  padding: 0.55rem 0.6rem;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${MUTED};
  border-bottom: 1px solid ${LINE};
  white-space: nowrap;
`;

export const Td = styled.td`
  padding: 0.6rem;
  border-bottom: 1px solid ${LINE_SOFT};
  font-size: 0.85rem;
  color: ${BODY};
  vertical-align: top;
`;

export const PersonName = styled.div`font-weight: 700; color: ${NAVY};`;
export const PersonMeta = styled.div`font-size: 0.76rem; color: ${MUTED};`;
export const SpotList = styled.ul`list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.25rem;`;
export const SpotLine = styled.li`display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem; font-size: 0.8rem;`;
export const ReminderCount = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  color: ${(p) => (p.$full ? '#991b1b' : BODY)};
`;
export const StoppedTag = styled.span`
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #e2e8f0;
  color: #475569;
  padding: 0.12rem 0.4rem;
`;

// ------------------------------------------------------------------ confirmation dialog

// The dialog's own buttons. `$secondary`/`$danger` are transient, so nothing but the element's real
// attributes reaches the DOM (ui.Button forwards `secondary`/`danger` as attributes, which React
// warns about).
export const DialogButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 0;
  padding: 0.6rem 0.9rem;
  font: inherit;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? '#64748b' : p.$danger ? '#dc2626' : '#47ABDB')};

  &:hover:not(:disabled) { background: ${(p) => (p.$secondary ? '#475569' : p.$danger ? '#b91c1c' : '#0056b3')}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// The person picker of a dialog: one row of the hit list (name bold, institution and email muted
// below it), the card of the person picked, and the small link beside it.
export const HitButton = styled.button`
  display: block;
  width: 100%;
  border: 0;
  background: ${(p) => (p.$active ? '#eff9fe' : '#fff')};
  padding: 0.4rem 0.6rem;
  font: inherit;
  font-size: 0.85rem;
  text-align: left;
  color: ${BODY};
  cursor: pointer;

  &:hover, &:focus-visible { background: #eff9fe; }
  strong { display: block; color: ${NAVY}; }
  span { display: block; font-size: 0.76rem; color: ${MUTED}; }
`;

export const PersonCard = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #bfe3f4;
  border-left: 4px solid ${ACCENT};
  background: #eff9fe;
  font-weight: 400;
`;

export const CreatedTag = styled.span`
  margin-left: 0.4rem;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
`;

export const LinkButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  color: #0369a1;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
`;

// One email of the dialog: the send choice, the address it lists, subject, body and preview.
export const EmailBlock = styled.div`
  border: 1px solid ${LINE};
  border-left: 4px solid ${ACCENT};
  background: ${FLAT};
  padding: 0.7rem 0.8rem;
  margin-bottom: 0.9rem;

  ${(p) => p.$multi && css`margin-top: 0.9rem;`}
`;

export const EmailHeader = styled.div`
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${MUTED};
  margin-bottom: 0.4rem;
`;

export const EmailSpots = styled.div`color: ${MUTED}; margin-top: 0.15rem;`;

export const FieldHint = styled.span`
  display: block;
  font-weight: 400;
  font-size: 0.72rem;
  color: ${MUTED};
  margin-top: 0.2rem;
`;
